/**
 * Typed environment accessors.
 * Always import from here — never use import.meta.env directly in app code.
 */
import { parseGatewayOriginPins } from './realtime-gateway';

/**
 * The value is passed in rather than looked up with `import.meta.env[key]`:
 * a dynamic key defeats Vite's static replacement, so it inlines the WHOLE
 * `VITE_*` object into the bundle — every key from every loaded env file,
 * read or not. That is how `VITE_API_PROXY_TARGET` (dev-only) and a stray
 * verification flag reached the published bundle. A static
 * `import.meta.env.VITE_X` becomes just that one literal.
 */
function requireEnv(key: keyof ImportMetaEnv, value: string | undefined): string {
  if (!value) {
    throw new Error(`[env] Missing required environment variable: ${key}`);
  }
  return value;
}

/**
 * A build-time boolean flag. Only the literal strings `true` / `false` are
 * recognised (case-insensitive, trimmed); anything else — unset, empty, `1`,
 * `yes` — falls back to `fallback`, so a typo can never flip a kill switch the
 * wrong way. Every `VITE_*_ENABLED` flag goes through here (phase6-contract §1.1).
 * The value is passed in as a static `import.meta.env.VITE_X` read — never
 * looked up with `import.meta.env[key]` — for the reason `requireEnv` gives.
 */
function envFlag(raw: string | undefined, fallback: boolean): boolean {
  if (typeof raw !== 'string') return fallback;
  const value = raw.trim().toLowerCase();
  if (value === 'true') return true;
  if (value === 'false') return false;
  return fallback;
}

/** Base URL of the ERPat REST API (no trailing slash). */
export const API_BASE_URL: string = requireEnv('VITE_API_BASE_URL', import.meta.env.VITE_API_BASE_URL);

/** True in dev builds (Vite dev server) — for dev-only diagnostics. */
export const IS_DEV: boolean = import.meta.env.DEV;

/**
 * True for a production build (`vite build`, i.e. the deployed PWA and every
 * native shell — `cap sync` packages the same `dist/`). The complement of
 * {@link IS_DEV}.
 */
export const IS_PROD: boolean = import.meta.env.PROD;

/**
 * Whether Google Analytics for Firebase is switched on for this build.
 * On by default. `VITE_FIREBASE_ANALYTICS=off` disables it entirely — no SDK
 * chunk is loaded, no gtag script, no requests — which is what a local
 * `vite preview` verification run or a white-label build without analytics
 * wants. Read once at build time, like every other VITE_* value.
 */
export const ANALYTICS_ENABLED: boolean =
  (import.meta.env.VITE_FIREBASE_ANALYTICS || '').trim().toLowerCase() !== 'off';

/** Syntry app version, injected from package.json at build time (see vite.config.ts). */
export const APP_VERSION: string = __APP_VERSION__;

/** ERPat OAuth2 / API client identifier. */
export const ERPAT_CLIENT_ID: string = requireEnv('VITE_ERPAT_CLIENT_ID', import.meta.env.VITE_ERPAT_CLIENT_ID);

/**
 * ERPat API client secret.
 * ⚠️  This value is embedded in the compiled JS bundle.
 * Treat it as a static API key; rotate it independently of server secrets.
 */
export const ERPAT_SECRET: string = requireEnv('VITE_ERPAT_SECRET', import.meta.env.VITE_ERPAT_SECRET);

/**
 * WHICH App Dash this build loads — the App Library **slug**, not the display
 * name. `syntry` is the catalog entry named "Syntry App"; the API matches on the
 * slug, and an unknown one is a 422 rather than a silent empty home, so this is
 * deliberately a fixed default rather than something guessed at runtime.
 *
 * Optional override, because the catalog is per-deployment data: a white-label
 * build can point at its own entry without a code change. Note `demo-syntry-mobile`
 * is a DIFFERENT catalog app — do not set that here expecting the real layout.
 */
export const ERPAT_APP_SLUG: string = import.meta.env.VITE_ERPAT_APP_SLUG || 'syntry';

/**
 * Optionally pin ONE dashboard by `dashboard_key` (e.g. `syntry_app`). Empty —
 * the normal case — lets the server choose the tenant's default published
 * dashboard, so a tenant can re-point their app without shipping a new build.
 */
export const ERPAT_APP_DASHBOARD_KEY: string = import.meta.env.VITE_ERPAT_APP_DASHBOARD_KEY || '';

/* ── Phase 6 build flags (phase6-contract.md §1.1) ─────────────────────────── */

/**
 * Opens the app to desktop form factors. While `false` (the default) the
 * mobile-only wall stands and the wall unregisters any service worker; the
 * persistent `?mobile_gate=off` QA bypass exists only in dev builds (DEC-086).
 * Flipping it to `true` in a production build needs the readiness sign-off
 * recorded in docs/desktop-notification-readiness.md (DEC-087).
 */
export const DESKTOP_APP_ENABLED: boolean = envFlag(import.meta.env.VITE_DESKTOP_APP_ENABLED, false);

/**
 * Build-time kill switch for the realtime socket: `false` leaves the realtime
 * service `disabled` (no bootstrap, no socket) and the inbox on REST polling.
 */
export const REALTIME_ENABLED: boolean = envFlag(import.meta.env.VITE_REALTIME_ENABLED, true);

/**
 * OPTIONAL, ADVISORY (DEC-102): the exact `ws(s)://host[:port]` origin(s) the
 * realtime bootstrap is EXPECTED to name — `wss://pushit.erpat.app` in a
 * production build, unset in dev / tests / `.env.verify`. NOT the gateway
 * itself: host, port and TLS always come from `GET /v1/api/me/realtime/bootstrap`
 * at runtime (there is deliberately no `VITE_*` override — see `types/env.d.ts`).
 * A bootstrap outside this list reads `gateway_unexpected` on the Live-updates
 * card and is STILL dialled (`services/realtime.service.ts`); a production
 * `vite build` refuses a loopback or `ws://` entry when the variable is set
 * (`vite.config.ts`). Parsed once, at build time, from one static read — like
 * every other VITE_* value.
 */
export const REALTIME_GATEWAY_ORIGINS: readonly string[] = parseGatewayOriginPins(
  import.meta.env.VITE_REALTIME_GATEWAY_ORIGINS,
).origins;

/**
 * Build-time kill switch for host (device/OS) notifications: `false` makes the
 * device boundary report `unsupported` — no permission prompt, no endpoint
 * registration, no OS alert — while the in-app toast keeps working.
 */
export const HOST_NOTIFICATIONS_ENABLED: boolean = envFlag(import.meta.env.VITE_HOST_NOTIFICATIONS_ENABLED, true);

/** The push-provider environment a NATIVE registration is made against. */
export type PushEnvironment = 'sandbox' | 'production';

/**
 * The `environment` sent on a NATIVE (`fcm` / `apns`) endpoint registration.
 * Defaults to `sandbox` while the only authored iOS entitlement is
 * `development`; a store build sets `VITE_PUSH_ENVIRONMENT=production`. Web
 * registrations always send `production` (ERPat parity) and never read this.
 * Only the two literal values are recognised (trimmed, lower-cased); anything
 * else falls back to `sandbox`, the safe direction.
 */
export const PUSH_ENVIRONMENT: PushEnvironment = ((): PushEnvironment => {
  const raw = import.meta.env.VITE_PUSH_ENVIRONMENT;
  const value = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  return value === 'production' ? 'production' : 'sandbox';
})();

/**
 * Whether this build's NATIVE shells carry a push-provider configuration, so
 * the push plugin's provider calls (`register()` / `unregister()`) may run
 * (DEC-105). Default `false`, the safe direction: the web / PWA build never
 * reads it, and a native build with it off still shows live alerts while it
 * runs — only background delivery is unavailable.
 *
 * Set `true` ONLY when every native shell synced from this build has its
 * provider configuration: Android `android/app/google-services.json` for the
 * `app.syntry` package (the stock `app/build.gradle` applies the google-services
 * plugin only when that file exists), iOS the APNs entitlement. One `dist/`
 * feeds both shells. On Android without the file, both calls start with
 * `FirebaseMessaging.getInstance()`, which throws, and Capacitor rethrows a
 * plugin method's exception on its handler thread — the app PROCESS dies; no
 * JS try/catch can contain it (the sign-out crash, reproduced 2026-10-19).
 */
export const NATIVE_PUSH_ENABLED: boolean = envFlag(import.meta.env.VITE_NATIVE_PUSH_ENABLED, false);

/* ── Offline attendance (specs/offline-attendance-v1, D6) ──────────────────── */

/**
 * This build's offline-capture MASTER switch for EVERY platform — the web /
 * PWA build and the native (Android / iOS) shells alike (default `true`).
 * `false` turns offline capture off everywhere (readiness
 * `online_only/build_disabled`): clock actions go to ERPat online, as before.
 *
 * It gates CAPTURE only, never uploads: punches already saved on the device are
 * still sent whatever it says, and until they have synced a new clock action is
 * refused rather than sent online ahead of them.
 *
 * `true` only makes the build CAPABLE; it is not the rollout. Capture is
 * further gated at run time by the company's ERPat setting
 * (`syntry_offline_attendance_enabled`, off by default, reported by
 * `GET /v1/api/me/attendance/offline-context`) and by a valid, installation-
 * bound grant — and the native shells also need
 * {@link OFFLINE_ATTENDANCE_NATIVE_ENABLED}.
 */
export const OFFLINE_ATTENDANCE_ENABLED: boolean = envFlag(import.meta.env.VITE_OFFLINE_ATTENDANCE_ENABLED, true);

/**
 * The extra gate the native (Android / iOS) shells need on top of the master
 * switch {@link OFFLINE_ATTENDANCE_ENABLED} (default `false`). It has no effect
 * on the web / PWA build, and none while the master switch is `false`. It stays
 * `false` until the real-device gate (P6) passes: browser unit tests do not
 * prove that the Android WebView keeps IndexedDB or that the Keystore /
 * Keychain key custody holds, so the native path ships and is unit-tested but
 * is not switched on by default. While it is `false` the native app clocks
 * online only (readiness `online_only/native_disabled`), once any punches
 * already saved on the device have synced. Like the master switch it gates
 * capture only — saved punches are still uploaded — and the ERPat setting
 * still applies.
 */
export const OFFLINE_ATTENDANCE_NATIVE_ENABLED: boolean = envFlag(
  import.meta.env.VITE_OFFLINE_ATTENDANCE_NATIVE_ENABLED,
  false,
);
